import React, { lazy, Suspense } from 'react';
import RoutePageLoader from '../../components/RoutePageLoader';
import AppSettingsPlaceholderContent from './AppSettingsPlaceholderContent';
import DefaultPageFallbackContent from './DefaultPageFallbackContent';

const DashboardHomeContent = lazy(() => import('./DashboardHomeContent'));
const TargetsContent = lazy(() => import('./TargetsContent'));
const RisksContent = lazy(() => import('./RisksContent'));
const ScansContent = lazy(() => import('./ScansContent'));
const ActiveScansContent = lazy(() => import('./ActiveScansContent'));
const EarlyDisclosuresContent = lazy(() => import('./EarlyDisclosuresContent'));
const EarlyDisclosuresGhostContent = lazy(() => import('./EarlyDisclosuresGhostContent'));
const SubscriptionContent = lazy(() => import('./SubscriptionContent'));
const SubscriptionOrgDetailContent = lazy(() => import('./SubscriptionOrgDetailContent'));
const ProfileSettingsContent = lazy(() => import('./ProfileSettingsContent'));
const SettingsConnectorsContent = lazy(() => import('./SettingsConnectorsContent'));
const SettingsManageUsersContent = lazy(() => import('./SettingsManageUsersContent'));
const SettingsDeploymentContent = lazy(() => import('./SettingsDeploymentContent'));
const SettingsUsersOrgsContent = lazy(() => import('./SettingsUsersOrgsContent'));
const SettingsSitesContent = lazy(() => import('./SettingsSitesContent'));
const SettingsScanNetworkContent = lazy(() => import('./SettingsScanNetworkContent'));
const SettingsScanNetworkOpsContent = lazy(() => import('./SettingsScanNetworkOpsContent'));
const SettingsPartnersContent = lazy(() => import('./SettingsPartnersContent'));
const RiskDetailContent = lazy(() => import('./RiskDetailContent'));
const ScanDetailContent = lazy(() => import('./ScanDetailContent'));
const SupportContent = lazy(() => import('./SupportContent'));
const AIIntelligenceContent = lazy(() => import('./AIIntelligenceContent'));
const ReportsContent = lazy(() => import('./ReportsContent'));
const SecurityEventsContent = lazy(() => import('./SecurityEventsContent'));
const XDRIntegrationsContent = lazy(() => import('./XDRIntegrationsContent'));
const OCVAContent = lazy(() => import('./OCVAPage'));

function renderActivePage({
  activePage,
  pageMeta,
  dashboard,
  risks,
  targets,
  profile,
  connectors,
  settings,
  scans,
  earlyDisclosures,
  subscription,
  support,
  pageComponents,
}) {
  const { isAppSettingsPage, PAGE_TITLES } = pageMeta;
  const { ManageUsersPageComponent } = pageComponents || {};

  if (activePage === 'dashboard') {
    return (
      <DashboardHomeContent
        warningMessage={dashboard.dataWarnings.length > 0 ? dashboard.dataWarnings[0] : ''}
        summary={dashboard.summary}
        summaryLoading={dashboard.summaryLoading}
        summaryLoadError={dashboard.summaryLoadError || ''}
        isDarkMode={dashboard.isDarkMode}
        canSeePartnerUi={dashboard.uiAccess.canSeePartnerUi}
        dashboardScopeQuery={dashboard.dashboardScopeQuery}
        universalSearchQuery={dashboard.universalSearchQuery}
        openScansPageWithFilter={dashboard.openScansPageWithFilter}
        openRisksPageWithFilters={dashboard.openRisksPageWithFilters}
        onClearUniversalSearch={dashboard.onClearUniversalSearch}
        vulnerabilities={dashboard.vulnerabilities}
        vulnerabilitiesLoading={dashboard.vulnerabilitiesLoading}
        vulnerabilitiesLoadError={dashboard.vulnerabilitiesLoadError || ''}
        topVulnerableHosts={dashboard.topVulnerableHosts}
        hostsLoading={dashboard.hostsLoading}
        hostsLoadError={dashboard.hostsLoadError || ''}
        chartIncidents={dashboard.chartIncidents}
        historyLoading={dashboard.historyLoading}
        historyLoadError={dashboard.historyLoadError || ''}
        overviewDateRange={dashboard.overviewDateRange}
      />
    );
  }

  if (activePage === 'risks') {
    return (
      <RisksContent
        filterSeed={risks.filterSeed}
        orgQuery={risks.orgQuery}
        universalSearchQuery={risks.universalSearchQuery}
      />
    );
  }

  if (activePage === 'riskDetail') {
    return (
      <RiskDetailContent
        riskId={risks.riskId}
        scannerHint={risks.scannerHint}
        seedRow={risks.seedRow}
        onBack={risks.onBack}
        userProfile={risks.userProfile}
      />
    );
  }

  if (activePage === 'scanDetail') {
    return (
      <ScanDetailContent
        scanId={scans.scanId}
        scanType={scans.scanType}
        targetLabel={scans.targetLabel}
        seedRow={scans.seedRow}
        onBack={scans.onBack}
      />
    );
  }

  if (activePage === 'targets') {
    return (
      <TargetsContent
        assetsWarningMessage={
          targets.dataWarnings.find((msg) => String(msg || '').startsWith('Assets unavailable:')) || ''
        }
        assetsInventory={targets.assetsInventory}
        universalSearchQuery={targets.universalSearchQuery}
        onInventoryRefresh={targets.onInventoryRefresh}
      />
    );
  }

  if (activePage === 'profileSettings') {
    return (
      <ProfileSettingsContent
        userProfile={profile.userProfile}
        onProfileChange={profile.onUserProfileChange}
        onLogout={profile.onLogout}
        forcedTab={profile.forcedTab}
      />
    );
  }

  if (activePage === 'settingsConnectors') {
    return (
      <SettingsConnectorsContent
        searchQuery={connectors.universalSearchQuery}
        onSearchQueryChange={connectors.onUniversalSearchQueryChange}
        onConfigureConnector={connectors.onConfigureConnector}
        onDeleteConnector={connectors.onDeleteConnector}
        onTestConnector={connectors.onTestConnector}
        refreshKey={connectors.connectorListRefreshKey}
        canManageConnectors={connectors.canManageConnectors}
        scannerTab={connectors.scannerTab}
        onScannerTabChange={connectors.onScannerTabChange}
      />
    );
  }

  if (activePage === 'settingsDeployment') {
    return <SettingsDeploymentContent />;
  }

  if (activePage === 'settingsUsersOrgs') {
    return <SettingsUsersOrgsContent userProfile={settings.userProfile} />;
  }

  if (activePage === 'settingsSites') {
    return <SettingsSitesContent userProfile={settings.userProfile} />;
  }

  if (activePage === 'settingsScanNetwork') {
    return <SettingsScanNetworkContent orgId={settings.headerSelectedOrgId} />;
  }

  if (activePage === 'settingsManageUsers') {
    return (
      <SettingsManageUsersContent
        ManageUsersPageComponent={ManageUsersPageComponent}
        userProfile={settings.userProfile}
        onProfileChange={settings.onUserProfileChange}
        onAddInviteUser={settings.openManageUsersInviteModal}
        usersRefreshKey={settings.manageUsersRefreshKey}
        onLogout={settings.onLogout}
        headerSelectedOrgId={settings.headerSelectedOrgId}
        headerSelectedOrgName={settings.headerSelectedOrgName}
      />
    );
  }

  if (activePage === 'settingsScanNetworkOps') {
    return <SettingsScanNetworkOpsContent />;
  }

  if (activePage === 'settingsPartners') {
    return <SettingsPartnersContent />;
  }

  if (isAppSettingsPage(activePage)) {
    return <AppSettingsPlaceholderContent pageTitle={PAGE_TITLES[activePage]} />;
  }

  if (activePage === 'scans') {
    return (
      <ScansContent
        initialScanTab={scans.scansInitialTab ?? scans.routeScanTab}
        onInitialScanTabConsumed={scans.consumeScansInitialTab}
        initialScanFilter={scans.scansInitialFilter}
        onInitialScanFilterConsumed={scans.consumeScansInitialFilter}
        initialScanLocation={scans.scansInitialLocation}
        refreshKey={scans.scansRefreshKey}
        orgQuery={scans.dashboardScopeQuery}
        headerSelectedOrgName={scans.headerSelectedOrgName}
        onClearHeaderOrg={scans.onClearHeaderOrg}
        universalSearchQuery={scans.universalSearchQuery}
        onUniversalSearchQueryChange={scans.onUniversalSearchQueryChange}
        isDarkMode={scans.isDarkMode}
        userProfile={scans.userProfile}
      />
    );
  }

  if (activePage === 'activeScans') {
    return (
      <ActiveScansContent
        refreshKey={scans.scansRefreshKey}
        orgQuery={scans.dashboardScopeQuery}
        universalSearchQuery={scans.universalSearchQuery}
        onUniversalSearchQueryChange={scans.onUniversalSearchQueryChange}
      />
    );
  }

  if (activePage === 'ocva') {
    return <OCVAContent />;
  }

  if (activePage === 'earlyDisclosures') {
    return (
      <EarlyDisclosuresContent
        onViewGhostCveExposures={earlyDisclosures.onViewGhostCveExposures}
        orgScopeQuery={earlyDisclosures.orgScopeQuery}
      />
    );
  }

  if (activePage === 'earlyDisclosures2') {
    return (
      <EarlyDisclosuresGhostContent
        exposuresFilterSeed={earlyDisclosures.ghostExposuresFilterSeed}
        onBackToDashboard={earlyDisclosures.onBackToEarlyDisclosures}
        orgScopeQuery={earlyDisclosures.orgScopeQuery}
      />
    );
  }

  if (activePage === 'subscription') {
    return (
      <SubscriptionContent
        subscriptionOrgs={subscription.subscriptionOrgs}
        subscriptionOrgsLoading={subscription.subscriptionOrgsLoading}
        subscriptionOrgsError={subscription.subscriptionOrgsError}
        onViewPlansBilling={subscription.onViewPlansBilling}
      />
    );
  }

  if (activePage === 'subscriptionOrgDetail') {
    return (
      <SubscriptionOrgDetailContent
        selectedOrg={subscription.selectedOrg}
        subscriptionCurrent={subscription.subscriptionCurrent}
        subscriptionCurrentLoading={subscription.subscriptionCurrentLoading}
        subscriptionCurrentError={subscription.subscriptionCurrentError}
        subscriptionUsage={subscription.subscriptionUsage}
        subscriptionUsageLoading={subscription.subscriptionUsageLoading}
        subscriptionUsageError={subscription.subscriptionUsageError}
        subscriptionSearch={subscription.subscriptionSearch}
        setSubscriptionSearch={subscription.setSubscriptionSearch}
        subscriptionSortConfig={subscription.subscriptionSortConfig}
        toggleSubscriptionSort={subscription.toggleSubscriptionSort}
        getSubscriptionSortIcon={subscription.getSubscriptionSortIcon}
        filteredSubscriptionSites={subscription.filteredSubscriptionSites}
        renderSubscriptionUsageSegments={subscription.renderSubscriptionUsageSegments}
      />
    );
  }

  if (activePage === 'support') {
    return (
      <SupportContent
        userProfile={support?.userProfile}
        uiAccess={support?.uiAccess}
      />
    );
  }

  if (activePage === 'aiIntelligence') {
    return <AIIntelligenceContent />;
  }

  if (activePage === 'reports') {
    return <ReportsContent orgQuery={dashboard.dashboardScopeQuery} />;
  }

  if (activePage === 'securityEvents' || activePage === 'alerts') {
    return <SecurityEventsContent />;
  }

  if (activePage === 'xdr') {
    return <XDRIntegrationsContent />;
  }

  return <DefaultPageFallbackContent pageTitle={PAGE_TITLES[activePage]} />;
}

export default function DashboardPageContentRouter(props) {
  return (
    <Suspense fallback={<RoutePageLoader />}>
      {renderActivePage(props)}
    </Suspense>
  );
}
